import * as L from 'leaflet';
import { DateTime } from 'luxon';
import { RESET_TIMESTEPS } from './events';
import { addClassNames } from './lib/utils/dom';
/**
 * CSS class for indicating the active status of a button.
 */
const RADIO_BUTTON_ACTIVE_CLASS = 'is-active';
/**
 * Radio buttons to jump to specific points in time.
 */
export class ControlRadioButtons extends L.Control {
    /**
     * Create a new radio buttons control instance.
     * @param  buttonOptions - Radio button options.
     * @param  maap - The M.A.A.P. instance.
     */
    constructor(buttonOptions, maap) {
        super({
            position: 'bottomright',
        });
        /**
         * The button DOM elements.
         */
        this.elsButtons = [];
        this.buttonOptions = buttonOptions.map((options) => ({
            text: options.text,
            datetime: DateTime.fromISO(options.datetime, { zone: 'utc' }),
        }));
        this.maap = maap;
        // set the initial state
        this.state = this.generateState(this.maap.time.currentTime());
        // reselect the active button every time the M.A.A.P. instance's timesteps
        // are reset
        this.maap.on(RESET_TIMESTEPS, () => {
            const dtCurrent = this.maap.time.currentTime();
            if (dtCurrent !== undefined) {
                this.selectButtonByDateTime(dtCurrent);
            }
        });
    }
    /**
     * Leaflet hook for adding the control.
     * @returns The rendered radio buttons control.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onAdd() {
        return this.renderEl();
    }
    /**
     * Render the radio buttons control DOM.
     * @returns The rendered radio buttons control.
     */
    renderEl() {
        // Create the container DOM element and add the CSS classes.
        const el = document.createElement('div');
        addClassNames(el, [
            'maap_c-control',
            'maap_p-bottom-controls__control',
            'maap_p-control-radio-buttons',
        ]);
        // Render the child button elements and add append them to the container.
        this.elsButtons = this.renderElsButtons();
        this.elsButtons.forEach((elButton) => el.appendChild(elButton));
        // Update the DOM according to the state for the first time.
        this.updateDOM();
        return el;
    }
    /**
     * Render the radio buttons' DOM.
     * @return The rendered radio buttons.
     */
    renderElsButtons() {
        return this.buttonOptions.map((option) => {
            // Create the DOM button element and add the CSS classes.
            const elButton = document.createElement('button');
            addClassNames(elButton, ['maap_p-control-radio-buttons__button']);
            // Set the button text and other options.
            elButton.innerHTML = option.text;
            elButton.type = 'button';
            // Set the click event listener.
            elButton.addEventListener('click', () => {
                this.selectButtonByDateTime(option.datetime);
            });
            return elButton;
        });
    }
    /**
     * Select a control radio button by it's datetime.
     * @param dtActive - The datetime of the button to select.
     */
    selectButtonByDateTime(dtActive) {
        this.maap.time.setTime(dtActive);
        this.state = this.generateState(dtActive);
        this.updateDOM();
    }
    /**
     * Generate the state, telling us which button is active, by passing a
     * datetime to select.
     * @param dtActive - The datetime to be selected.
     * @returns The generated state.
     */
    generateState(dtActive) {
        return this.buttonOptions.map(({ datetime }) => {
            return {
                isActive: dtActive ? datetime.equals(dtActive) : false,
            };
        });
    }
    /**
     * Update the buttons DOM according to their state.
     */
    updateDOM() {
        this.state.forEach(({ isActive }, index) => {
            const elButton = this.elsButtons[index];
            if (isActive) {
                elButton.classList.add(RADIO_BUTTON_ACTIVE_CLASS);
            }
            else {
                elButton.classList.remove(RADIO_BUTTON_ACTIVE_CLASS);
            }
        });
    }
}
//# sourceMappingURL=ControlRadioButtons.js.map